import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';
import imgPrefix from '../imgConfig';
import '../styles/Header.css';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState({ technicians: [], services: [] });
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchTimeout = useRef(null);
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [lang, setLang] = useState(localStorage.getItem('lang') || i18n.language || 'zh');

  useEffect(() => {
    if (lang) {
      i18n.changeLanguage(lang);
      localStorage.setItem('lang', lang);
    }
  }, [lang, i18n]);

  // 搜索函数
  const performSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults({ technicians: [], services: [] });
      setShowResults(false);
      return;
    }

    setLoading(true);
    try {
      const currentLang = i18n.language || 'zh';
      // 并行搜索教练和服务项目
      const [techniciansRes, servicesRes] = await Promise.all([
        // 搜索教练
        axios.get(`/jiaolianxinxi/autoSort?lang=${currentLang}`).then(res => {
          if (res.data.code === 0) {
            return res.data.data.list.filter(tech => 
              tech.jiaolianxingming.toLowerCase().includes(query.toLowerCase()) ||
              (tech.zhuanye && tech.zhuanye.toLowerCase().includes(query.toLowerCase()))
            ).slice(0, 5); // 限制显示5个结果
          }
          return [];
        }),
        // 搜索服务项目
        axios.get(`/jianshenxiangmu/autoSort?lang=${currentLang}`).then(res => {
          if (res.data.code === 0) {
            return res.data.data.list.filter(service => 
              service.xiangmumingcheng.toLowerCase().includes(query.toLowerCase()) ||
              (service.xiangmuleixing && service.xiangmuleixing.toLowerCase().includes(query.toLowerCase()))
            ).slice(0, 5); // 限制显示5个结果
          }
          return [];
        })
      ]);

      setSearchResults({
        technicians: techniciansRes,
        services: servicesRes
      });
      setShowResults(true);
    } catch (error) {
      console.error('search failed:', error);
      setSearchResults({ technicians: [], services: [] });
    } finally {
      setLoading(false);
    }
  };

  // 处理搜索输入变化
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // 清除之前的搜索定时器
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // 设置新的搜索定时器，防抖处理
    searchTimeout.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  // 处理搜索按钮点击
  const handleSearchClick = () => {
    if (searchTerm.trim()) {
      performSearch(searchTerm);
    }
  };

  // 处理键盘事件
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  // 点击教练结果
  const handleTechnicianClick = (technician) => {
    navigate(`/technician/${technician.jiaolianbianhao}`);
    setShowResults(false);
    setSearchTerm('');
  };

  // 点击服务结果
  const handleServiceClick = (service) => {
    navigate(`/service/${service.id}`);
    setShowResults(false);
    setSearchTerm('');
  };

  // 点击外部关闭搜索结果
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.header-search-container')) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="header">
      <div className="header-left">
        <div className="header-search-container">
          <i className="icon-search"></i>
          <input 
            type="text" 
            placeholder={t('header.searchPlaceholder')} 
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            className="search-input"
          />
          {/* Language Switcher */}
          <select className="lang-switcher" value={lang} onChange={e => setLang(e.target.value)} style={{ marginLeft: 12 }}>
            <option value="zh">中文</option>
            <option value="en">English</option>
            <option value="th">ไทย</option>
          </select>
          
          {/* 搜索结果下拉框 */}
          {showResults && (
            <div className="search-results-dropdown">
              {loading ? (
                <div className="search-loading">{t('header.search')}</div>
              ) : (
                <>
                  {/* 教练搜索结果 */}
                  {searchResults.technicians.length > 0 && (
                    <div className="search-section">
                      <div className="search-section-title">{t('nav.home')}</div>
                      {searchResults.technicians.map(technician => (
                        <div 
                          key={technician.id} 
                          className="search-result-item"
                          onClick={() => handleTechnicianClick(technician)}
                        >
                          <img 
                            src={`${imgPrefix}/${technician.jiaoliantupian}`} 
                            alt={technician.jiaolianxingming}
                            className="search-result-avatar"
                          />
                          <div className="search-result-info">
                            <div className="search-result-name">{technician.jiaolianxingming}</div>
                            <div className="search-result-desc">
                              {technician.zhuanye || 'Pro'} • {technician.jiaoliandengji || 'Senior'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* 服务项目搜索结果 */}
                  {searchResults.services.length > 0 && (
                    <div className="search-section">
                      <div className="search-section-title">{t('nav.home')}</div>
                      {searchResults.services.map(service => (
                        <div 
                          key={service.id} 
                          className="search-result-item"
                          onClick={() => handleServiceClick(service)}
                        >
                          <img 
                            src={`${imgPrefix}/${service.xiangmutupian}`} 
                            alt={service.xiangmumingcheng}
                            className="search-result-avatar"
                          />
                          <div className="search-result-info">
                            <div className="search-result-name">{service.xiangmumingcheng}</div>
                            <div className="search-result-desc">
                              {service.xiangmuleixing} • ¥{service.jiage || 'N/A'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* 无搜索结果 */}
                  {searchResults.technicians.length === 0 && searchResults.services.length === 0 && searchTerm.trim() && (
                    <div className="search-no-results">
                      {t('header.noResult')}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="header-right">
        <span onClick={handleSearchClick} className="search-button">{t('header.search')}</span>
      </div>
    </header>
  );
};

export default Header;
